(() => {
  /** @type {any} */
  const windowAny = window;
  /** @type {any} */
  const recommendationsUtils = windowAny.EcommRecommendationsUtils;
  if (!recommendationsUtils) return;

  function addRecommendationLoader() {
    if (document.getElementById('load-container')) return false;

    const loadContainer = document.createElement('div');
    loadContainer.className = 'load-container';
    loadContainer.id = 'load-container';

    const loadSpinner = document.createElement('div');
    loadSpinner.className = 'load';

    loadContainer.appendChild(loadSpinner);
    document.body.appendChild(loadContainer);
    return true;
  }

  /**
   * @param {boolean} ownsLoader
   */
  function removeRecommendationLoader(ownsLoader) {
    if (!ownsLoader) return;
    window.setTimeout(() => {
      document.querySelectorAll('#load-container').forEach((loader) => loader.remove());
    }, 150);
  }

  /**
   * Defer non-interactive recommendation work so initial page controls can bind and paint first.
   * @param {() => void} callback
   */
  function runWhenBrowserIsReady(callback) {
    if ('requestIdleCallback' in window) {
      /** @type {any} */ (window).requestIdleCallback(callback, { timeout: 500 });
      return;
    }

    globalThis.setTimeout(callback, 120);
  }

  // profileKey is the profile key (e.g. "p1", "p2", "p3") from the event detail
  /**
   * Read selected diet labels + preferred diet token for a profile key from the "mijn_gegevens" payload
   * (same structure as the guest sessionStorage payload).
   *
   * @param {string} profileKey
   * @returns {{ selectedDietLabels: string[], preferredDietToken: string, preferredDietOrderTokens: string[] }}
   */
  function readDietInfoFromMijnGegevens(profileKey) {
    // Read the "mijn_gegevens" payload from the script tag - Because the logged-in user has a customer metafield "mijn_gegevens"
    const payload = recommendationsUtils.readJsonFromScriptTag('[data-ecomm-mijn-gegevens-json]', '[ECOMM][LOGGED-IN]');
    // The profiles are the profiles that are in the payload ( mijn_gegevens )
    const profiles = Array.isArray(payload?.profiles) ? payload.profiles : [];
    // Find the profile that matches the profile key
    const profile =
      profiles.find((/** @type {any} */ p) => String(p?.profileKey || '').trim() === String(profileKey || '').trim()) || null;

    /** @type {any} */
    // Get the algorithm API from the window object
    const algorithmApi = windowAny.EcommRecommendationsAlgorithm;
    // The diet type entries are the diet type entries that are in the profile
    const dietTypeEntries = Array.isArray(profile?.dietType) ? profile.dietType : [];
    // The preferred diet order tokens are the preferred diet order tokens that are in the profile
    // This is the function that will get the preferred diet order tokens from the algorithm API - example: ['zoutarm', 'koolhydraatarm', 'Vegetarisch', 'caloriearm', 'eiwitrijk', 'natriumarm']
    const preferredDietOrderTokens =
      algorithmApi && typeof algorithmApi.getDietPreferenceOrderTokens === 'function'
        ? algorithmApi.getDietPreferenceOrderTokens(dietTypeEntries)
        : [];
    // The preferred diet token is the first preferred diet order token
    const preferredDietToken = preferredDietOrderTokens[0] || '';

    // The selected diet labels are the selected diet labels that are in the profile
    const selectedDietLabels = Array.from(
      new Set(
        (Array.isArray(profile?.dietType) ? profile.dietType : [])
          .map((/** @type {any} */ d) => String(d?.diet || '').trim())
          .filter(Boolean)
      )
    );

    return { selectedDietLabels, preferredDietToken, preferredDietOrderTokens };
  }


  /**
   * @param {any|null} data
   * @param {string} selectedProfileKey
   * @returns {{ orderedIds: string[], idSet: Set<string> }}
   */
  function getRecommendedProductIdsForProfile(data, selectedProfileKey) {
    // The profiles are the profiles that are in the payload ( recommendations )
    const profiles = Array.isArray(data?.profiles) ? data.profiles : [];
    // Find the profile that matches the profile key
    const profile =
      profiles.find((/** @type {any} */ p) => String(p?.profileKey || '').trim() === String(selectedProfileKey || '').trim()) ||
      null;

    // The recommendations are the recommendations that are in the profile
    const recommendations = Array.isArray(profile?.recommendations) ? profile.recommendations : [];
    /** @type {string[]} */
    const orderedIds = [];
    recommendations.forEach((/** @type {any} */ rec) => {
      const id = recommendationsUtils.convertRecommendationProductIdToId(String(rec?.productId || '').trim());
      if (!id) return;
      orderedIds.push(id);
    });

    return { orderedIds, idSet: new Set(orderedIds) };
  }

  /**
   * Apply logged-in recommendations for a selected profile key.
   * This assumes Liquid rendered recommendation <li> items with:
   * - data-ecomm-recommendation-item
   * - data-ecomm-profile-key="p1|p2|..."
   * - data-product-id="123"
   *
   * @param {string} selectedProfileKey
   */
  function applyLoggedInRecommendationsForProfile(selectedProfileKey) {
    // console.log('[ECOMM][LOGGED-IN] applyLoggedInRecommendationsForProfile:start', { selectedProfileKey });
    // Read the "churned recommendations" - Because the logged-in user has a customer metafield "recommendations"
    const data = recommendationsUtils.readJsonFromScriptTag('[data-ecomm-recommendations-json]', '[ECOMM][LOGGED-IN]');
    if (!data) {
      return;
    }
    // Get the recommended product ids for the profile
    const { orderedIds } = getRecommendedProductIdsForProfile(data, selectedProfileKey);
    // Get all the recommendation items from the DOM
    const allRecItems = Array.from(document.querySelectorAll('[data-ecomm-recommendation-item]')).filter(
      (el) => el instanceof HTMLElement
    );

    // Only handle logged-in recommendation items (exclude guest metaobject items)
    const loggedInRecItems = allRecItems.filter(
      (el) => String(el.getAttribute('data-ecomm-profile-key') || '').trim().toLowerCase() !== 'guest'
    );
    if (!loggedInRecItems.length) {
      return;
    }

    // Persist a stable original order once, so repeated reorders are deterministic.
    loggedInRecItems.forEach((el, i) => {
      if (!el.dataset.ecommOriginalIndex) el.dataset.ecommOriginalIndex = String(i);
    });

    // The list parent is the parent element of the logged-in recommendation items ( <ul> element inside of main-collection.liquid )
    const listParent = loggedInRecItems[0]?.parentElement;
    // If the list parent is not an HTMLElement, return
    if (!(listParent instanceof HTMLElement)) return;

    // If the selected profile has no recommendation items rendered in the DOM (common when
    // customer.metafields.custom.recommendations only contains p1), fall back to p1 items
    // so the customer never sees an empty grid when switching person.
    const selectedKey = String(selectedProfileKey || '').trim();
    // Get the selected profile items from the DOM
    const selectedProfileItems = loggedInRecItems.filter(
      (el) => String(el.getAttribute('data-ecomm-profile-key') || '').trim() === selectedKey
    );
    // If the selected profile items are not found, fall back to p1 items
    const fallbackProfileKey = 'p1';
    // The effective profile key is the profile key that will be used to show the recommendation items
    const effectiveProfileKey =
      selectedProfileItems.length
        ? selectedKey
        : fallbackProfileKey;

    const applySignature = `${effectiveProfileKey}:${listParent.children.length}:${loggedInRecItems.length}`;
    if (listParent.dataset.ecommLoggedInAppliedSignature === applySignature) return;

    // 1) Toggle visibility: show only the selected profile's recommendation items
    loggedInRecItems.forEach((item) => {
      const itemProfileKey = String(item.getAttribute('data-ecomm-profile-key') || '').trim();
      item.classList.toggle('no-disp', itemProfileKey !== effectiveProfileKey);
    });

    // 2) Reorder: for the active profile, prefer algorithm-based ordering (same logic as guest).
    /** @type {any} */
    const algorithmApi = windowAny.EcommRecommendationsAlgorithm;
    // Read the "mijn_gegevens" payload from the script tag - Because the logged-in user has a customer metafield "mijn_gegevens"
    const mijnGegevensPayload = recommendationsUtils.readJsonFromScriptTag('[data-ecomm-mijn-gegevens-json]', '[ECOMM][LOGGED-IN]');
    // Build the picker context from the payload based on the selected profile key
    // This picker returns a context object that contains the profile, the preferred diet order tokens, and the preferences
    // The preferences are the diet tokens, the kitchen tokens, the portion grams, the meal slots, the meals count, the soup count, and the dessert count
    const pickerContext =
      typeof recommendationsUtils.buildThuysVersRecommendationPickerContextFromPayload === 'function'
        ? recommendationsUtils.buildThuysVersRecommendationPickerContextFromPayload(mijnGegevensPayload, selectedProfileKey)
        : null;

    /** @type {HTMLElement[]} */
    // Get the active profile items from the DOM
    const activeProfileItems = loggedInRecItems.filter(
      (el) => String(el.getAttribute('data-ecomm-profile-key') || '').trim() === effectiveProfileKey
    );

    /** @type {HTMLElement[]} */
    let activeOrdered = [];
    /** @type {Set<string>} */
    let activeChosenProductIdSet = new Set();

    if (
      pickerContext &&
      algorithmApi &&
      typeof algorithmApi.pickMealsAndExtrasBasedOnProfileKeyPreference === 'function' &&
      activeProfileItems.length
    ) {
      // Read the churned recommendations ordered product ids for the given profile key
      // Because you're logged in, we can use the churned recommendations to prioritize the recommendation items
      const churnedPriorityIds =
        typeof recommendationsUtils.readChurnedRecommendationOrderedIds === 'function'
          ? recommendationsUtils.readChurnedRecommendationOrderedIds(selectedProfileKey, '[ECOMM][LOGGED-IN]')
          : [];

      // Apply churned ordering (if present) as a priority input for stable tiebreaking.
      /** @type {HTMLElement[]} */
      const activeItemsForPicker =
        typeof recommendationsUtils.prioritizeElementsByProductId === 'function'
          ? recommendationsUtils.prioritizeElementsByProductId(activeProfileItems, churnedPriorityIds)
          : activeProfileItems;

      // This is the algorithm that will pick the best items for the profile from the payload ( churned recommendations )
      const pickResult = algorithmApi.pickMealsAndExtrasBasedOnProfileKeyPreference(activeItemsForPicker, pickerContext.prefs);
      // The preferred diet order tokens are the tokens that we are going to use to order the items
      const preferredDietOrderTokens = Array.isArray(pickerContext.preferredDietOrderTokens) ? pickerContext.preferredDietOrderTokens : [];
      // The profile portion grams for rest is the portion grams that we are going to use to order the rest of the items
      const profilePortionGramsForRest =
        typeof recommendationsUtils.parseProfilePortionGramsFromSurveyProfile === 'function'
          ? recommendationsUtils.parseProfilePortionGramsFromSurveyProfile(pickerContext.profile)
          : null;

      // This is the function that will apply the picker result to the active profile items
      const applied =
        typeof recommendationsUtils.applyPickerResultToRecommendItems === 'function'
          ? recommendationsUtils.applyPickerResultToRecommendItems(activeProfileItems, pickResult, preferredDietOrderTokens, profilePortionGramsForRest)
          : { orderedItems: [...pickResult.orderedChosen, ...pickResult.orderedRest], pickedProductIdSet: new Set() };

      // The next dom order is the ordered items that will be appended to the list parent
      activeOrdered = applied.orderedItems;
      // The chosen product id set is the set of product ids that have been chosen
      activeChosenProductIdSet = applied.pickedProductIdSet;
    } else if (orderedIds.length) {
      // Fallback: original logged-in behavior (churned recommendations order)
      // This is the fallback behavior that will be used if the picker context is not available
      orderedIds.forEach((id) => {
        const match = activeProfileItems.find((el) => String(el.getAttribute('data-product-id') || '').trim() === String(id));
        if (match) activeOrdered.push(match);
      });
      activeChosenProductIdSet = new Set(activeOrdered.map((el) => String(el.getAttribute('data-product-id') || '').trim()).filter(Boolean));
    } else {
      // Last resort: keep stable DOM order for active profile items
      activeOrdered = activeProfileItems;
      activeChosenProductIdSet = new Set(activeOrdered.map((el) => String(el.getAttribute('data-product-id') || '').trim()).filter(Boolean));
    }

    // Remaining: keep existing DOM order (stable)
    const remaining = Array.from(listParent.children)
      .filter((el) => el instanceof HTMLElement)
      .filter((el) => !activeOrdered.includes(/** @type {HTMLElement} */(el)));

    // Sort remaining "normal" products by preferred diet (stable):
    // Read the diet info from the "mijn_gegevens" payload based on the selected profile key
    const dietInfo = readDietInfoFromMijnGegevens(selectedProfileKey);
    // The preferred diet order tokens are the tokens that we are going to use to order the items
    const preferredDietOrderTokens = Array.isArray(dietInfo?.preferredDietOrderTokens) ? dietInfo.preferredDietOrderTokens : [];
    // The remaining recommendation items are the recommendation items that are in the remaining items
    const remainingRecommendationItems = remaining.filter((el) => el.hasAttribute('data-ecomm-recommendation-item'));
    // The remaining normal items are the normal items that are in the remaining items
    const remainingNormalItems = remaining.filter((el) => !el.hasAttribute('data-ecomm-recommendation-item'));

    // The ordered normal items are the normal items that are in the remaining items
    const orderedNormalItems =
      typeof recommendationsUtils.sortElementsByPreferredDiet === 'function'
        ? recommendationsUtils.sortElementsByPreferredDiet(remainingNormalItems, preferredDietOrderTokens)
        : remainingNormalItems;

    // Create a document fragment to append the ordered items to the list parent
    const fragment = document.createDocumentFragment();
    // Append the ordered items to the document fragment
    [...activeOrdered, ...remainingRecommendationItems, ...orderedNormalItems].forEach((el) =>
      fragment.appendChild(el)
    );
    // Append the document fragment to the list parent
    listParent.appendChild(fragment);

    // Apply diet badge label based on the logged-in preferences payload (mijn_gegevens)
    // Apply badges to ALL items in the list, not just recommended ones, so products matching selectedDietLabels get badges
    const selectedDietLabels = dietInfo.selectedDietLabels;
    const allItemsForBadges = Array.from(listParent.children).filter((el) => el instanceof HTMLElement);
    if (typeof recommendationsUtils.applyRecommendationBadges === 'function') {
      recommendationsUtils.applyRecommendationBadges(allItemsForBadges, selectedDietLabels);
    }

    // 3) Hide duplicates from the normal grid (defensive; Liquid also filters these out)
    Array.from(listParent.children).forEach((child) => {
      if (!(child instanceof HTMLElement)) return;
      if (child.hasAttribute('data-ecomm-recommendation-item')) return;
      const productId = String(child.getAttribute('data-product-id') || '').trim();
      child.classList.toggle('no-disp', Boolean(productId) && activeChosenProductIdSet.has(productId));
    });

    listParent.dataset.ecommLoggedInAppliedSignature = applySignature;

  }

  /**
   * Derive the active profile key from event or checked radio.
   * @param {any} eventDetail
   * @returns {string}
   */
  function resolveActiveProfileKey(eventDetail) {
    const fromEvent = String(eventDetail?.profileKey || '').trim();
    if (fromEvent) return fromEvent;
    const checked = document.querySelector('input[name="ecomm-profile"]:checked');
    if (checked instanceof HTMLInputElement) return String(checked.value || '').trim();
    return '';
  }

  /** Recommendations JSON is only embedded for customers with `recommendations` metafield (see ecomm-recommendations-overview-block.liquid). */
  function hasLoggedInRecommendationsDataset() {
    const el = document.querySelector('[data-ecomm-recommendations-json]');
    if (!(el instanceof HTMLElement)) return false;
    return Boolean(String(el.textContent || '').trim());
  }

  // Trigger on profile change (from ecomm-recommendations-utility.js)
  window.addEventListener('ecomm-profile-changed', (event) => {
    const detail = /** @type {any} */ (event)?.detail || {};
    const profileKey = resolveActiveProfileKey(detail);
    if (!profileKey) return;
    const source = String(detail.source || '');
    scheduleCurrentProfileApply({
      profileKey,
      showLoader: source === 'change',
      waitForIdle: source !== 'change',
    });
  });

  // Infinite-scroll appends can introduce recommendation or normal items after the initial pass.
  window.addEventListener('collection-page-appended', () => {
    const profileKey = resolveActiveProfileKey(null);
    if (!profileKey) return;
    scheduleCurrentProfileApply({ profileKey, waitForIdle: false });
  });

  let scheduledApply = false;
  let pendingProfileKey = '';
  let pendingOwnsLoader = false;
  let pendingWaitForIdle = true;

  /**
   * @param {{ profileKey?: string, showLoader?: boolean, waitForIdle?: boolean }} [options]
   */
  const scheduleCurrentProfileApply = (options = {}) => {
    if (!hasLoggedInRecommendationsDataset()) {
      return;
    }

    const optionProfileKey = String(options.profileKey || '').trim();
    if (optionProfileKey) pendingProfileKey = optionProfileKey;

    if (options.showLoader && !pendingOwnsLoader) {
      pendingOwnsLoader = addRecommendationLoader();
    }

    if (options.waitForIdle === false) {
      pendingWaitForIdle = false;
    }

    if (scheduledApply) return;
    scheduledApply = true;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const runApply = () => {
          scheduledApply = false;
          const profileKey = pendingProfileKey || resolveActiveProfileKey(null);
          const ownsLoader = pendingOwnsLoader;
          pendingProfileKey = '';
          pendingOwnsLoader = false;
          pendingWaitForIdle = true;

          try {
            if (!profileKey) return;
            applyLoggedInRecommendationsForProfile(profileKey);
          } finally {
            removeRecommendationLoader(ownsLoader);
          }
        };

        if (pendingWaitForIdle) {
          runWhenBrowserIsReady(runApply);
          return;
        }

        try {
          runApply();
        } catch (error) {
          pendingOwnsLoader = false;
          pendingWaitForIdle = true;
          throw error;
        }
      });
    });
  };

  // Section morphs can add logged-in recommendation rows after this script has loaded.
  document.addEventListener('collection-results-rendered', () => scheduleCurrentProfileApply({ showLoader: false }));
  window.addEventListener('ecomm-recommendations-updated', () => scheduleCurrentProfileApply({ showLoader: false }));

  // Initial run (in case the toggle event hasn't fired yet)
  try {
    scheduleCurrentProfileApply({ showLoader: false });
  } catch (error) {
    // console.warn('[ECOMM][LOGGED-IN] initial apply failed', error);
  }

  const bindToggle = () => recommendationsUtils.ensurePersonToggleRecommendationDispatchBound?.();
  requestAnimationFrame(() => requestAnimationFrame(bindToggle));
  document.addEventListener('collection-results-rendered', bindToggle);
})();
